<template>
    <div>

        <rkm-modal
            extra-large
            ref="RkmModal"
            title="Prescrição de medicamento"
        >
            <rkm-form
                ref="RkmForm"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="12">
                        <rkm-form-select
                            @change="handleIsManualChange"
                            label="Tipo de prescrição"
                            v-model="model.isManual"
                        >
                            <rkm-form-select-option :value="false">Padronizado</rkm-form-select-option>
                            <rkm-form-select-option :value="true">Não padronizado</rkm-form-select-option>
                        </rkm-form-select>
                    </rkm-col>
                </rkm-row>

                <template v-if="model.isManual">
                    <rkm-row>
                        <rkm-col>
                            <rkm-form-select
                                :options="data.form.prescricaoMedicamentos.receitaTipos"
                                :validations="validations.receitaTipos"
                                label="Receita"
                                text-field="nome"
                                v-model="model.receitaTipoId"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col cols="auto">
                            <rkm-form-checkbox
                                :disabled="model.isRemoverImpressao"
                                :label="false"
                                :value="true"
                                v-model="model.isImprimirSeparado"
                            >
                                Imprimir separado
                            </rkm-form-checkbox>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="12">
                            <rkm-text-editor
                                :maxlength="validations.descricao.max"
                                :validations="validations.descricao"
                                label="Descrição"
                                rows="6"
                                v-model="model.descricao"
                                v-on:ready="quill => editorQuillDescricao = quill"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="12">
                            <rkm-text-editor
                                :validations="validations.justificativa"
                                label="Justificativa"
                                rows="6"
                                v-model="model.justificativa"
                                v-on:ready="quill => editorQuillJustificativa = quill"
                            />
                        </rkm-col>
                    </rkm-row>
                </template>

                <template v-else>
                    <rkm-row>
                        <rkm-col cols="12">
                            <rkm-form-input-autocomplete
                                :repository="repositories.material"
                                :search-fields="fields"
                                :request-query-string-params="{
                                    grupo_tipo_id: enums.tipoGrupo.MEDICAMENTO,
                                    visualizar_remume: data.additionals.visualizarRemume,
                                    is_exibir_receita: true
                                }"
                                :text-field="m => `${m.descricao}${getMedicamentoPrograma(m)}`"
                                :validations="validations.material"
                                label="Medicamento"
                                uppercase
                                v-model="model.material"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="4">
                            <rkm-form-input
                                :value="model.material ? model.material.viaAdministracao : null"
                                disabled
                                label="Via de administração"
                            />
                        </rkm-col>

                        <rkm-col>
                            <rkm-form-input
                                :validations="validations.dose"
                                label="Dose"
                                v-model="model.dose"
                            />
                        </rkm-col>

                        <rkm-col>
                            <rkm-form-select
                                :options="data.form.unidadesMedida"
                                :validations="validations.unidadeMedida"
                                label="Unidade de medida"
                                show-default-option
                                text-field="nome"
                                v-model="model.unidadeMedidaId"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col cols="auto">
                            <rkm-form-checkbox
                                :disabled="model.isUsoContinuo"
                                :label="false"
                                :value="true"
                                @change="handleDoseUnicaChange"
                                v-model="model.isDoseUnica"
                            >
                                Dose única
                            </rkm-form-checkbox>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="12">
                            <rkm-form-radio-group
                                :options="data.form.prescricaoMedicamentos.frequenciasDose"
                                :validations="validations.frequenciaDose"
                                @change="handleFrequenciaDoseChange"
                                label="Frequência da dose"
                                v-model="model.frequenciaDose"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row v-if="model.frequenciaDose === enums.frequenciaDose.INTERVALO">
                        <rkm-col cols="6">
                            <rkm-form-input
                                :disabled="model.isDoseUnica"
                                :validations="validations.intervalo"
                                @input="() => model.intervaloValue = null"
                                append="hora(s)"
                                name="Intervalo"
                                number
                                v-model="model.intervalo"
                            >
                                <rkm-input-group-prepend slot="prepend">
                                    <rkm-input-group-text>A cada</rkm-input-group-text>

                                    <rkm-form-radio-group
                                        :disabled="model.isDoseUnica"
                                        :options="data.form.prescricaoMedicamentos.intervalos"
                                        @change="(value) => model.intervalo = value"
                                        button-color="outline-primary"
                                        buttons
                                        v-model="model.intervaloMedidaTempoId"
                                    />
                                </rkm-input-group-prepend>
                            </rkm-form-input>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row v-else-if="model.frequenciaDose === enums.frequenciaDose.FREQUENCIA">
                        <rkm-col cols="auto">
                            <rkm-form-input
                                :disabled="model.isDoseUnica"
                                :validations="validations.frequenciaVezes"
                                @input="() => model.frequenciaMedidaTempoId = null"
                                append="vez(es)"
                                name="Frequência"
                                number
                                v-model="model.frequenciaVezes"
                            >
                                <rkm-input-group-prepend slot="prepend">
                                    <rkm-form-radio-group
                                        :disabled="model.isDoseUnica"
                                        :options="data.form.prescricaoMedicamentos.frequencias"
                                        @change="(value) => model.frequenciaVezes = value"
                                        button-color="outline-primary"
                                        buttons
                                        v-model="model.frequenciaMedidaTempoId"
                                    />
                                </rkm-input-group-prepend>
                            </rkm-form-input>
                        </rkm-col>

                        <rkm-col>
                            <rkm-form-input
                                :disabled="model.isDoseUnica"
                                :validations="validations.intervalo"
                                name="Intervalo"
                                number
                                prepend="a cada"
                                v-model="model.intervalo"
                            />
                        </rkm-col>

                        <rkm-col>
                            <rkm-form-select
                                :disabled="model.isDoseUnica"
                                :options="data.form.prescricaoMedicamentos.unidadesMedidaTempo"
                                :validations="validations.intervaloMedidaTempo"
                                name="Período"
                                text-field="nome"
                                v-model="model.intervaloMedidaTempoId"
                                value-field="id"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row v-else-if="model.frequenciaDose === enums.frequenciaDose.TURNO">
                        <rkm-col cols="auto">
                            <rkm-form-radio-group
                                :disabled="model.isDoseUnica"
                                :options="data.form.prescricaoMedicamentos.turnos"
                                :validations="validations.turno"
                                button-color="outline-primary"
                                buttons
                                text-field="nome"
                                v-model="model.turnoId"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col>
                            <rkm-form-input
                                :disabled="model.isDoseUnica"
                                :validations="validations.intervalo"
                                name="Quantidade"
                                number
                                prepend="a cada"
                                v-model="model.intervalo"
                            />
                        </rkm-col>

                        <rkm-col>
                            <rkm-form-select
                                :disabled="model.isDoseUnica"
                                :options="data.form.prescricaoMedicamentos.unidadesMedidaTempo"
                                :validations="validations.intervaloMedidaTempo"
                                name="Período"
                                text-field="nome"
                                v-model="model.intervaloMedidaTempoId"
                                value-field="id"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <rkm-form-input
                                :value="posologia"
                                disabled
                                label="Posologia"
                            />
                        </rkm-col>

                        <rkm-col cols="auto">
                            <rkm-form-checkbox
                                :disabled="model.isDoseUnica"
                                :label="false"
                                :value="true"
                                @change="handleUsoContinuoChange"
                                v-model="model.isUsoContinuo"
                            >
                                Uso contínuo
                            </rkm-form-checkbox>
                        </rkm-col>

                        <rkm-col cols="auto">
                            <rkm-form-checkbox
                                :disabled="model.isRemoverImpressao"
                                :label="false"
                                :value="true"
                                v-model="model.isImprimirSeparado"
                            >
                                Imprimir separado
                            </rkm-form-checkbox>
                        </rkm-col>

                        <rkm-col cols="auto">
                            <rkm-form-checkbox
                                :disabled="model.isImprimirSeparado"
                                :label="false"
                                :value="true"
                                v-model="model.isRemoverImpressao"
                            >
                                Remover da impressão
                            </rkm-form-checkbox>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <rkm-form-date-picker
                                :min-date="data.additionals.dataAtual"
                                :validations="validations.inicio"
                                label="Início do tratamento"
                                v-model="model.inicio"
                            />
                        </rkm-col>

                        <rkm-col cols="auto">
                            <rkm-form-input
                                :disabled="model.isDoseUnica || model.isUsoContinuo || model.duracaoMedidaTempoId === 'indeterminado'"
                                :validations="validations.duracao"
                                label="Duração"
                                number
                                v-model="model.duracao"
                            >
                                <rkm-input-group-append slot="append">
                                    <rkm-form-radio-group
                                        :disabled="model.isDoseUnica || model.isUsoContinuo"
                                        :options="data.form.prescricaoMedicamentos.unidadesMedidaTempo"
                                        :validations="validations.duracaoMedidaTempo"
                                        @change="handlePeriodoChange"
                                        button-color="outline-primary"
                                        buttons
                                        name="Período"
                                        text-field="nome"
                                        v-model="model.duracaoMedidaTempoId"
                                        value-field="id"
                                    >
                                        <rkm-form-radio
                                            color="primary"
                                            outline
                                            value="indeterminado"
                                        >
                                            Indeterminado
                                        </rkm-form-radio>
                                    </rkm-form-radio-group>
                                </rkm-input-group-append>
                            </rkm-form-input>
                        </rkm-col>

                        <rkm-col>
                            <rkm-form-date-picker
                                :value="conclusaoTratamento"
                                disabled
                                label="Conclusão"
                            />
                        </rkm-col>
                    </rkm-row>
                </template>

                <rkm-row>
                    <rkm-col cols="12">
                        <rkm-text-editor
                            :validations="validations.recomendacoes"
                            label="Recomendações"
                            rows="4"
                            v-model="model.recomendacoes"
                            v-on:ready="quill => editorQuill = quill"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-form>

            <template v-slot:footer="{cancel}">
                <rkm-button @click="cancel">
                    Fechar
                </rkm-button>

                <rkm-button
                    @click="save(false)"
                    color="primary"
                    :disabled="!!isLoading"
                >
                    {{item ? 'Salvar' : 'Incluir'}}
                </rkm-button>

                <rkm-button
                    v-if="!item"
                    @click="save(true)"
                    color="primary"
                    :disabled="!!isLoading"
                >
                    {{ 'Incluir e fechar' }}
                </rkm-button>
            </template>

            <rkm-row>
                <rkm-col cols="12">
                    <rkm-list
                        :items="data.model.prescricaoMedicamentos"
                        hide-filters
                        hide-pagination
                        item-cursor="default"
                    >
                        <template v-slot="{item}">
                            <rkm-row v-if="item.isManual">
                                <rkm-col>
                                    <strong>Descrição</strong><br/>
                                    <div v-html="formatDescricao(item.descricao)"></div>
                                </rkm-col>

                                <rkm-col v-if="item.justificativa">
                                    <strong>Justificativa</strong><br/>
                                    <div v-html="formatDescricao(item.justificativa)"></div>
                                </rkm-col>
                                <rkm-col v-else><strong>Justificativa</strong><br/>-</rkm-col>
                            </rkm-row>

                            <template v-else>
                                <rkm-row>
                                    <rkm-col>
                                        <strong>Medicamento</strong><br/>{{item.material.descricao}}
                                    </rkm-col>

                                    <rkm-col>
                                        <strong>Via de administração</strong><br/>{{item.material.viaAdministracao}}
                                    </rkm-col>

                                    <rkm-col>
                                        <strong>Unidade de medida</strong><br/>{{item.unidadeMedida}}
                                    </rkm-col>

                                    <rkm-col>
                                        <strong>Tipo</strong><br/>{{item.material.receitaTipo}}
                                    </rkm-col>
                                </rkm-row>
                            </template>

                            <rkm-row>
                                <rkm-col>
                                    <strong>Recomendações</strong><br/>
                                    <div v-html="formatDescricao(item.recomendacoes)"></div>
                                </rkm-col>
                                <rkm-col v-if="item.isManual">
                                    <strong>Tipo</strong><br/>{{ item.receitaTipoId === enums.receitaTipo.ESPECIAL ? 'Especial' : 'Comum' }}
                                </rkm-col>
                            </rkm-row>
                        </template>

                        <template v-slot:trailling="{index, item}">

                            <rkm-button
                                @click="edit(item)"
                                color="primary"
                                small
                                title="Editar"
                            >
                                <rkm-icon icon="pen" />
                            </rkm-button>

                            <rkm-button
                                @click="destroyItem(item, index)"
                                color="danger"
                                small
                                title="Excluir"
                            >
                                <rkm-icon icon="trash" />
                            </rkm-button>
                    </template>
                    </rkm-list>
                </rkm-col>
            </rkm-row>
        </rkm-modal>

        <ModalPrescricao
            :data="data"
            ref="ModalPrescricao"
            @save="saveDuplicated(true)"
        />
    </div>
</template>

<script>
// Components.
import ModalPrescricao from './ModalPrescricao'


// Enums.
import frequenciaDose from '@/enums/ambulatorio/frequenciaDose'
import medidaTempo from '@/enums/cadastro/medidaTempo'
import receitaTipo from '@/enums/cadastro/medicamento/tipoReceita'
import tipoGrupo from '@/enums/cadastro/grupo/tipoGrupo'

// Mixins.
import {object, repository, stringCase} from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import {mapActions, mapGetters} from 'vuex'

export default {
    components: {
        ModalPrescricao,
    },
    computed: {
         ...mapGetters({ isLoading: 'getLoading' }),
        posologia() {
            let posologia = 'Nenhuma informação adicionada'
            const unidadeMedida = this.data.form.unidadesMedida.find(unidade => unidade.id === this.model.unidadeMedidaId)

            if (this.model.frequenciaDose === this.enums.frequenciaDose.INTERVALO && this.model.intervalo) {
                if (this.model.intervalo == 1) {
                    posologia = unidadeMedida
                        ? `${this.model.dose} ${unidadeMedida.nome.toLowerCase()} a cada hora`
                        : `${this.model.dose ? `${this.model.dose} (dose)` : ''} a cadaz hora`
                } else {
                    posologia = unidadeMedida
                        ? `${this.model.dose} ${unidadeMedida.nome.toLowerCase()} a cada ${this.model.intervalo} hora(s)`
                        : `${this.model.dose ? `${this.model.dose} (dose)` : ''} a cada ${this.model.intervalo} hora(s)`
                }
            }

            else if (this.model.frequenciaDose === this.enums.frequenciaDose.FREQUENCIA) {
                if (this.model.frequenciaVezes && this.model.intervalo) {
                    const intervaloMedidaTempo = this.data.form.prescricaoMedicamentos.unidadesMedidaTempo
                        .find(unidadeMedidaTempo => unidadeMedidaTempo.id === this.model.intervaloMedidaTempoId)

                    if (this.model.intervalo == 1 && this.model.intervaloMedidaTempoId === this.enums.medidaTempo.DIAS) {
                        posologia = unidadeMedida
                            ? `${this.model.dose} ${unidadeMedida.nome.toLowerCase()},  ${this.model.frequenciaVezes} vez(es) ao dia`
                            : `${this.model.dose ? `${this.model.dose} (dose),` : ''} ${this.model.frequenciaVezes} vez(es) ao dia`
                    } else {
                        posologia = unidadeMedida
                            ? `${this.model.dose} ${unidadeMedida.nome.toLowerCase()}, ${this.model.frequenciaVezes} vez(es) a cada ${this.model.intervalo} ${intervaloMedidaTempo.nome}`
                            : `${this.model.dose ? `${this.model.dose} (dose),` : ''} ${this.model.frequenciaVezes} vez(es) a cada ${this.model.intervalo} ${intervaloMedidaTempo.nome}`
                    }
                }
            }
            else if (this.model.frequenciaDose === this.enums.frequenciaDose.TURNO) {

                if (this.model.turnoId && this.model.intervalo) {
                    const turno = this.data.form.prescricaoMedicamentos.turnos
                        .find(turno => turno.id === this.model.turnoId)

                    const intervaloMedidaTempo = this.data.form.prescricaoMedicamentos.unidadesMedidaTempo
                        .find(unidadeMedidaTempo => unidadeMedidaTempo.id === this.model.intervaloMedidaTempoId)

                    if (this.model.intervalo == 1 && this.model.intervaloMedidaTempoId === this.enums.medidaTempo.DIAS) {
                        posologia = unidadeMedida
                            ? `${this.model.dose} ${unidadeMedida.nome.toLowerCase()} pela ${turno.nome.toLowerCase()}`
                            : `${this.model.dose ? `${this.model.dose} (dose),` : ''} pela ${turno.nome.toLowerCase()}`
                    } else {
                        posologia = unidadeMedida
                            ? `${this.model.dose} ${unidadeMedida.nome.toLowerCase()} pela ${turno.nome.toLowerCase()} a cada ${this.model.intervalo} ${intervaloMedidaTempo.nome}`
                            : `${this.model.dose ? `${this.model.dose} (dose),` : ''} pela ${turno.nome.toLowerCase()} a cada ${this.model.intervalo} ${intervaloMedidaTempo.nome}`
                    }
                }
            }
            else if (this.model.isDoseUnica) {
                posologia = `${this.model.dose ? `${this.model.dose},` : ''} dose única`
            }

            return posologia
        },
        conclusaoTratamento() {
            let conclusaoTratamento = null

            if (this.model.inicio) {
                if (this.model.isDoseUnica) {
                    conclusaoTratamento = this.others.moment(this.model.inicio).format('YYYY-MM-DD')
                }
                else if (this.model.duracao && this.model.duracaoMedidaTempoId) {
                    if (this.model.duracaoMedidaTempoId === this.enums.medidaTempo.DIAS) {
                        conclusaoTratamento = this.others.moment(this.model.inicio).add(this.model.duracao, 'days').format('YYYY-MM-DD')
                    }
                    else if (this.model.duracaoMedidaTempoId === this.enums.medidaTempo.SEMANAS) {
                        conclusaoTratamento = this.others.moment(this.model.inicio).add(this.model.duracao, 'weeks').format('YYYY-MM-DD')
                    }
                    else if (this.model.duracaoMedidaTempoId === this.enums.medidaTempo.MESES) {
                        conclusaoTratamento = this.others.moment(this.model.inicio).add(this.model.duracao, 'months').format('YYYY-MM-DD')
                    }
                }
            }

            return conclusaoTratamento
        },
        validations() {
            let duracaoMaxValue = null

            if (this.model.duracaoMedidaTempoId === this.enums.medidaTempo.DIAS) {
                duracaoMaxValue = 180
            }
            if (this.model.duracaoMedidaTempoId === this.enums.medidaTempo.SEMANAS) {
                duracaoMaxValue = 24
            }
            if (this.model.duracaoMedidaTempoId === this.enums.medidaTempo.MESES) {
                duracaoMaxValue = 6
            }

            return {
                descricao: {
                    required: this.data.additionals.receitaNaoPadronizadaJustificativaObrigatoria ? true : false,
                    max_without_html: this.model.receitaTipoId === this.enums.receitaTipo.ESPECIAL ? 500 : 5000,
                },
                justificativa: {
                    required: this.data.additionals.receitaNaoPadronizadaJustificativaObrigatoria ? true : false,
                    max_without_html: 300,
                },
                material: {required: true},
                receitaTipos: {required: true},
                dose: {
                    required: true,
                    max: 50,
                },
                frequenciaDose: {
                    required: !this.model.isDoseUnica
                },
                unidadeMedida: {required: true},
                intervalo: {
                    required: true,
                    max: 2,
                },
                intervaloMedidaTempo: {required: true},
                frequenciaVezes: {
                    required: true,
                    max: 2,
                },
                turno: {required: true},
                inicio: {required: true},
                duracao: {
                    required: true,
                    max: 3,
                    max_value: duracaoMaxValue,
                },
                duracaoMedidaTempo: {required: true},
                recomendacoes: {max_without_html: 500},
            }
        },
    },
    created() {
        this.repositories = {
            material: this.getConectaSusRepository('cadastro.material'),
            atendimento: this.getConectaSusRepository('ambulatorio.atendimento'),
        }
    },
    data: () => ({
        enums: {
            frequenciaDose,
            medidaTempo,
            receitaTipo,
            tipoGrupo,
        },
        fields: [
            {key: 'codigo_material', searchable: true},
            {key: 'descricao', searchable: true},
        ],
        item: null,
        model: {
            isManual: false,
            receitaTipoId: receitaTipo.COMUM,
            descricao: null,
            justificativa: null,
            material: null,
            dose: null,
            isDoseUnica: false,
            frequenciaDose: null,
            intervalo: null,
            intervaloMedidaTempoId: medidaTempo.DIAS,
            frequenciaVezes: null,
            turnoId: null,
            isUsoContinuo: false,
            isImprimirSeparado: false,
            isRemoverImpressao: false,
            inicio: null,
            duracao: null,
            duracaoMedidaTempoId: null,
            recomendacoes: null,
            unidadeMedidaId: null,
            unidadeMedida: null,
        },
        others: {moment},
        editorQuill: null,
        editorQuillDescricao: null,
        editorQuillJustificativa: null,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open(item = null) {
            this.item = item

            if (this.item) {
                this.edit()
            } else {
                this.create()
            }

            this.$refs.RkmModal.show()
        },
        create() {
            this.resetForm()
        },
        edit(item = null) {
            if(item) {
                this.item = item
            }

            this.model = this.clone({
                checked: this.item.checked,
                isManual: this.item.isManual,
                receitaTipoId: this.item.receitaTipoId,
                descricao: this.item.descricao,
                justificativa: this.item.justificativa,
                material: this.item.material,
                dose: this.item.dose,
                isDoseUnica: this.item.isDoseUnica,
                frequenciaDose: this.item.frequenciaDose,
                intervalo: this.item.intervalo,
                intervaloMedidaTempoId: this.item.intervaloMedidaTempoId,
                frequenciaVezes: this.item.frequenciaVezes,
                turnoId: this.item.turnoId,
                unidadeMedidaId: this.item.unidadeMedidaId,
                unidadeMedida: this.item.unidadeMedida,
                isUsoContinuo: this.item.isUsoContinuo,
                isImprimirSeparado: this.item.isImprimirSeparado,
                isRemoverImpressao: this.item.isRemoverImpressao,
                inicio: this.item.inicio ? this.others.moment(this.item.inicio, 'DD/MM/YYYY').format('YYYY-MM-DD') : null,
                duracao: Number.isInteger(Number(this.model.duracao)) ? parseInt(this.model.duracao) : null,
                duracaoMedidaTempoId: this.item.duracaoMedidaTempoId,
                recomendacoes: this.item.recomendacoes,
            })
        },
        store() {
            const unidadeMedida = this.data.form.unidadesMedida.find(unidade => unidade.id == this.model.unidadeMedidaId)

            this.data.model.prescricaoMedicamentos.push({
                checked: this.model.isImprimirSeparado,
                isManual: this.model.isManual,
                manualId: this.model.isManual ? moment().format('x') : null,
                receitaTipoId: this.model.receitaTipoId,
                descricao: this.model.descricao,
                justificativa: this.model.justificativa,
                material: this.model.material ? {
                    id: this.model.material.id,
                    descricao: this.model.material.descricao,
                    viaAdministracao: this.model.material.viaAdministracao,
                    formaFarmaceutica: this.model.material.formaFarmaceutica,
                    livroRegistroTipoId: this.model.material.livroRegistroTipoId,
                    receitaTipoId: this.model.material.receitaTipoId ?? null,
                    quantidade: this.model.material.quantidade ?? null,
                    apresentacao: this.model.material.apresentacaoNome ?? null,
                    receitaTipo: this.model.material.receitaTipo ?? null,
                    altoCusto: this.model.material.altoCusto ?? null,
                    programaRemedioEmCasa: this.model.material.programaRemedioEmCasa ?? null,
                    acaoJudicial: this.model.material.acaoJudicial ?? null,
                    remume: this.model.material.remume ?? null,
                } : null,
                dose: this.model.dose,
                isDoseUnica: this.model.isDoseUnica,
                frequenciaDose: this.model.frequenciaDose,
                intervalo: this.model.intervalo,

                intervaloMedidaTempoId: this.model.frequenciaDose === this.enums.frequenciaDose.INTERVALO
                    ? this.enums.medidaTempo.HORAS
                    : this.model.intervaloMedidaTempoId,

                frequenciaVezes: this.model.frequenciaVezes,
                turnoId: this.model.turnoId,
                unidadeMedidaId: this.model.unidadeMedidaId,
                unidadeMedida: unidadeMedida ? unidadeMedida.nome : null,
                isUsoContinuo: this.model.isUsoContinuo,
                isImprimirSeparado: this.model.isImprimirSeparado,
                isRemoverImpressao: this.model.isRemoverImpressao,
                inicio: this.model.inicio ? this.others.moment(this.model.inicio).format('DD/MM/YYYY') : null,
                duracao: Number.isInteger(Number(this.model.duracao)) ? parseInt(this.model.duracao) : null,
                duracaoMedidaTempoId: this.model.duracaoMedidaTempoId,
                recomendacoes: this.model.recomendacoes,
                posologia: this.posologia,
            })
        },
        update() {
            const unidadeMedida = this.data.form.unidadesMedida.find(unidade => unidade.id == this.model.unidadeMedidaId)

            Object.assign(this.item, {
                checked: this.model.checked === true ? true : this.model.isImprimirSeparado,
                isManual: this.model.isManual,
                receitaTipoId: this.model.receitaTipoId,
                descricao: this.model.descricao,
                justificativa: this.model.justificativa,
                material: this.model.material ? {
                    id: this.model.material.id,
                    descricao: this.model.material.descricao,
                    viaAdministracao: this.model.material.viaAdministracao,
                    formaFarmaceutica: this.model.material.formaFarmaceutica,
                    livroRegistroTipoId: this.model.material.livroRegistroTipoId,
                    receitaTipoId: this.model.material.receitaTipoId ?? null,
                    quantidade: this.model.material.quantidade ?? null,
                    apresentacao: this.model.material.apresentacaoNome ?? null,
                    receitaTipo: this.model.material.receitaTipo ?? null,
                    altoCusto: this.model.material.altoCusto ?? null,
                    programaRemedioEmCasa: this.model.material.programaRemedioEmCasa ?? null,
                    acaoJudicial: this.model.material.acaoJudicial ?? null,
                    remume: this.model.material.remume ?? null,
                } : null,
                dose: this.model.dose,
                isDoseUnica: this.model.isDoseUnica,
                frequenciaDose: this.model.frequenciaDose,
                intervalo: this.model.intervalo,

                intervaloMedidaTempoId: this.model.frequenciaDose === this.enums.frequenciaDose.INTERVALO
                    ? this.enums.medidaTempo.HORAS
                    : this.model.intervaloMedidaTempoId,

                frequenciaVezes: this.model.frequenciaVezes,
                turnoId: this.model.turnoId,
                unidadeMedidaId: this.model.unidadeMedidaId,
                unidadeMedida: unidadeMedida ? unidadeMedida.nome : null,
                isUsoContinuo: this.model.isUsoContinuo,
                isImprimirSeparado: this.model.isImprimirSeparado,
                isRemoverImpressao: this.model.isRemoverImpressao,
                inicio: this.model.inicio ? this.others.moment(this.model.inicio).format('DD/MM/YYYY') : null,
                duracao: this.model.duracao,
                duracaoMedidaTempoId: this.model.duracaoMedidaTempoId,
                recomendacoes: this.model.recomendacoes,
                posologia: this.posologia
            })
        },
        organizaPorTamanhoDaRecomendacao() {
            this.data.model.prescricaoMedicamentos.sort((a, b) => {
                const quantidadeA = a.recomendacoes ? a.recomendacoes.length : 0;
                const quantidadeB = b.recomendacoes ? b.recomendacoes.length : 0;
                return quantidadeB - quantidadeA;
            });
        },
        save(exit) {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    const request = {
                        pacienteId: this.data.additionals.paciente.id,
                        medicamentoId: this.model.material?.id,
                    }

                    this.setLoading()

                    this.repositories.atendimento.prescricaoMedicamento(this.camelObjectKeysToSnakeRecursive(request))
                        .then(response => {

                            const include = () => {

                                if (this.item) {
                                    this.update()
                                } else {
                                    this.store()
                                }

                                this.$nextTick().then(() =>  {

                                    this.organizaPorTamanhoDaRecomendacao();

                                    if (exit || this.item) {
                                        this.$refs.RkmModal.hide()
                                    } else {
                                        this.resetForm()
                                        this.$refs.RkmForm.resetValidator()
                                    }
                                })
                            }

                            if (response.data) {

                                if (this.data.additionals.permitePrescricaoRepetida) {

                                    this.$refs.ModalPrescricao.open(response.data)
                                } else {

                                    this.showAlert('Este paciente já possui uma prescrição para o medicamento e dentro do prazo de uso.', "Atenção");
                                }
                            } else {
                                include()
                            }
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        saveDuplicated(exit) {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    if (this.item) {
                        this.update()
                    } else {
                        this.store()
                    }

                    this.$nextTick().then(() =>  {

                        this.organizaPorTamanhoDaRecomendacao();

                        if (exit || this.item) {
                            this.$refs.RkmModal.hide()
                        } else {
                            this.resetForm()
                            this.$refs.RkmForm.resetValidator()
                        }
                    })
                })
        },
        resetForm() {
            this.model.isManual = false
            this.model.receitaTipoId = this.enums.receitaTipo.COMUM
            this.model.descricao = null
            this.model.justificativa = null
            this.model.material = null
            this.model.dose = null
            this.model.isDoseUnica = false
            this.model.frequenciaDose = null
            this.model.intervalo = null
            this.model.intervaloMedidaTempoId = null
            this.model.frequenciaVezes = null
            this.model.turnoId = null
            this.model.unidadeMedidaId = null
            this.model.unidadeMedida = null
            this.model.isUsoContinuo = false
            this.model.isImprimirSeparado = false
            this.model.isRemoverImpressao = false
            this.model.inicio = this.others.moment(this.data.additionals.dataAtual, 'DD/MM/YYYY').format('YYYY-MM-DD'),
            this.model.duracao = null
            this.model.duracaoMedidaTempoId = null
            this.model.recomendacoes = null
        },
        handleIsManualChange() {
            this.model.receitaTipoId = this.enums.receitaTipo.COMUM
            this.model.justificativa = null
            this.model.descricao = null
            this.model.material = null
            this.model.dose = null
            this.model.isDoseUnica = false
            this.model.frequenciaDose = null
            this.model.intervalo = null
            this.model.intervaloMedidaTempoId = null
            this.model.frequenciaVezes = null
            this.model.turnoId = null
            this.model.unidadeMedidaId = null
            this.model.unidadeMedida = null
            this.model.isUsoContinuo = false
            this.model.isImprimirSeparado = false
            this.model.isRemoverImpressao = false
            this.model.inicio = this.model.isManual ? null : this.others.moment(this.data.additionals.dataAtual, 'DD/MM/YYYY').toDate()
            this.model.duracao = null
            this.model.duracaoMedidaTempoId = null
            this.model.recomendacoes = null
        },
        handleDoseUnicaChange(value) {
            this.model.intervalo = null
            this.model.intervaloMedidaTempoId = null
            this.model.frequenciaVezes = null
            this.model.turnoId = null
            this.model.isUsoContinuo = false
            this.model.isImprimirSeparado = false
            this.model.isRemoverImpressao = false

            if (value === true) {
                this.model.duracao = 1
                this.model.duracaoMedidaTempoId = this.enums.medidaTempo.DIAS
            } else {
                this.model.duracao = null
                this.model.duracaoMedidaTempoId = null
            }
        },
        handleFrequenciaDoseChange() {
            this.model.intervalo = null
            this.model.intervaloMedidaTempoId = this.enums.medidaTempo.DIAS
            this.model.frequenciaVezes = null
            this.model.turnoId = null
        },
        handleUsoContinuoChange(value) {
            this.model.isDoseUnica = false
            this.model.inicio = this.others.moment(this.data.additionals.dataAtual, 'DD/MM/YYYY').format('YYYY-MM-DD')
            this.model.duracao = null
            this.model.duracaoMedidaTempoId = value ? 'indeterminado' : null
        },
        handlePeriodoChange(value) {
            if (value === 'indeterminado') {
                this.model.duracao = null
            }
        },
        destroyItem(item) {

            const objIndex = this.data.model.prescricaoMedicamentos.findIndex((obj => item.material ? (obj.material.id == item.material.id) : (obj.manualId == item.manualId)));

            const destroy = async () => this.data.model.prescricaoMedicamentos.splice(objIndex, 1)

            this.showDeleteConfirmation(`Deseja realmente excluir a prescrição de medicamento?`, destroy,  this.handleError)
        },
        formatDescricao(descricao) {

            if (descricao) {
                return descricao.replace(/\n/g, '<br>');
            }

            return null
        },
        getMedicamentoPrograma(medicamento) {

            const programas = [
                medicamento.altoCusto,
                medicamento.programaRemedioEmCasa,
                medicamento.acaoJudicial,
                medicamento.remume
            ].filter(p => p !== '')

            if (programas.length === 0) {

                return ''
            }

            return ` (${programas.join(', ')})`
        }
    },
    mixins: [object, repository, stringCase],
    props: {
        data: {required: true, type: Object},
    },
    watch: {
        'model.material'(value) {

            if (value && !this.model.unidadeMedidaId) {

                this.model.unidadeMedidaId = value.prescricaoUnidadeMedidaId
            }
        },
        'model.recomendacoes'() {

            const max = this.validations.recomendacoes?.max_without_html

            if (this.editorQuill && max && this.editorQuill.getLength() > max + 1) {
                this.editorQuill.deleteText(max, this.editorQuill.getLength())
            }
        },
        'model.descricao'() {

            const max = this.validations.descricao?.max_without_html

            if (this.editorQuillDescricao && max && this.editorQuillDescricao.getLength() > max + 1) {
                this.editorQuillDescricao.deleteText(max, this.editorQuillDescricao.getLength())
            }
        },
        'model.justificativa'() {

            const max = this.validations.justificativa?.max_without_html

            if (this.editorQuillJustificativa && max && this.editorQuillJustificativa.getLength() > max + 1) {
                this.editorQuillJustificativa.deleteText(max, this.editorQuillJustificativa.getLength())
            }
        }
    },
}
</script>